import React, { useMemo } from 'react';
import { DELIVERY_TYPES } from 'src/constants/deliveryTypes';
import { PAGE_FROM } from 'src/constants/main';
import { TimeController } from 'src/controllers/TimeController/TimeController';
import { localeStore } from 'src/mobx/localesStore';
import { IBranch } from 'src/types/branch';
import { formatPrice } from 'src/utils/formatter';
import { getPreorderIsAvailable } from 'src/utils/helpers';
import { WorkSlots } from 'src/view/screens/BranchesScreen/components/WorkSlots/WorkSlots';
import BranchDeliveryError from '../BranchDeliveryError/BranchDeliveryError';
import BranchDeliverySkeleton from '../BranchDeliverySkeleton/BranchDeliverySkeleton';

interface Props {
  branchNotCoveredAndGeoDisabled?: boolean;
  loading: boolean;
  branch?: IBranch | null;
  fromPage?: string;
}

const BranchDeliveryInfo: React.FC<Props> = ({ branch, loading, branchNotCoveredAndGeoDisabled, ...props }) => {
  const preorder_message = useMemo(() => getPreorderIsAvailable(branch), [branch]);
  const timeController = new TimeController({ branch, deliveryType: DELIVERY_TYPES.DELIVERY });
  const [status, isBranchWorking] = timeController.getWorkStatus();

  if (loading) {
    return <BranchDeliverySkeleton />;
  }

  if (!branch) return <BranchDeliveryError branchNotCoveredAndGeoDisabled={Boolean(branchNotCoveredAndGeoDisabled)} />;

  return (
    <>
      {/* Specifiy your address popup */}
      {props.fromPage === PAGE_FROM.SMS ? (
        <div className="specify-exact-location-container">
          <div className="specify-exact-location">{localeStore.t('specifiy_your_address')}</div>
        </div>
      ) : null}

      <div className="d-flex flex-column">
        <div className="d-flex flex-row align-items-center branches-header">
          {/* Preparation  */}
          {branch.working_now ? (
            <p className="branches-title text-size-20 family-semibold">
              ~{branch.preparation?.delivery?.gap} {localeStore.t(branch.preparation?.delivery?.unit || '')}
            </p>
          ) : (
            <p className="branches-title family-semibold">{localeStore.t('branch_closed')}</p>
          )}
          {/* Preorder */}
          {preorder_message ? (
            <div className="branches-preorder-popup family-semibold background-gray color-white ml-3 d-inline-block">
              {preorder_message}
            </div>
          ) : null}
        </div>

        {/* Busy */}
        {branch.is_busy ? (
          <div className="branches-busy family-semibold background-danger color-white d-inline-block">
            {localeStore.t('branch_busy_status')}
          </div>
        ) : null}

        {/* Work Slots */}
        <WorkSlots work_slots={branch.work_slots} status={status} isBranchWorking={isBranchWorking} />

        <div className="branches-order_info row">
          {/* Min order */}
          <div className="col col-4">
            <span className="branches-head family-semibold color-gray uppercase">{localeStore.t('min_order')}</span>
            <p className="branches-price family-semibold color-dark">{formatPrice(branch.min_order)}</p>
          </div>
          {/* Delivery fee */}
          <div className="col col-4">
            <span className="branches-head family-semibold color-gray uppercase">
              {localeStore.t('txt_delivery_fee')}
            </span>
            <p className="branches-price family-semibold color-dark">{formatPrice(branch.delivery_fee)}</p>
          </div>
          {/* Free Delivery */}
          {branch.is_free_delivery ? (
            <div className="col col-4">
              <span className="branches-head family-semibold color-gray uppercase">
                {localeStore.t('txt_delivery_free_from')}
              </span>
              <p className="branches-price family-semibold color-dark">{formatPrice(branch.free_delivery)}</p>
            </div>
          ) : null}
        </div>
        {/* Free Delivery Message */}
        {branch.is_free_delivery ? (
          <p className="branches-message_info color-lightgray text-size-10">
            {localeStore.t('vat_excl_free_delivery')}
          </p>
        ) : null}
      </div>
    </>
  );
};

export default BranchDeliveryInfo;
